import { useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import type {
  BuildingSearchFilters,
  BuildingSearchSort,
  ViewType,
} from '../types/buildingFilters.types'

interface UrlState {
  filters: BuildingSearchFilters
  view: ViewType
  sort: BuildingSearchSort[]
  page: number
  pageSize: number
}

// Helper to serialize filters to URL params
const filtersToUrlParams = (
  filters: BuildingSearchFilters
): URLSearchParams => {
  const params = new URLSearchParams()

  // Location fields - consistent with Land filters
  if (filters.location_search)
    params.set('location_search', filters.location_search)
  if (filters.location_type) params.set('location_type', filters.location_type)
  if (filters.municipality) params.set('municipality', filters.municipality)
  if (filters.district) params.set('district', filters.district)
  if (filters.region) params.set('region', filters.region)
  if (filters.npa) params.set('npa', filters.npa)

  // Legacy location fields for API compatibility
  if (filters.city_name) params.set('city_name', filters.city_name)
  if (filters.municipality_name)
    params.set('municipality_name', filters.municipality_name)
  if (filters.district_name) params.set('district_name', filters.district_name)
  if (filters.region_name) params.set('region_name', filters.region_name)
  if (filters.address_full) params.set('address_full', filters.address_full)
  if (filters.building_id) params.set('building_id', filters.building_id)
  if (filters.egid) params.set('egid', filters.egid.toString())

  // Array fields (location IDs)
  if (filters.city_id?.length) params.set('city_id', filters.city_id.join(','))
  if (filters.municipality_id?.length)
    params.set('municipality_id', filters.municipality_id.join(','))
  if (filters.district_id?.length)
    params.set('district_id', filters.district_id.join(','))
  if (filters.region_id?.length)
    params.set('region_id', filters.region_id.join(','))

  // Array fields for multi-select dropdowns
  if (filters.category?.length)
    params.set('category', filters.category.join(','))
  if (filters.type?.length) params.set('type', filters.type.join(','))
  if (filters.status) params.set('status', filters.status)
  if (filters.classification?.length)
    params.set('classification', filters.classification.join(','))
  if (filters.popety_classification?.length)
    params.set('popety_classification', filters.popety_classification.join(','))
  if (filters.building_period?.length)
    params.set('building_period', filters.building_period.join(','))
  if (filters.renovation_period?.length)
    params.set('renovation_period', filters.renovation_period.join(','))
  if (filters.building_permits_classification?.length)
    params.set(
      'building_permits_classification',
      filters.building_permits_classification.join(',')
    )

  // Energy filters - arrays
  if (filters.heater_generator?.length)
    params.set('heater_generator', filters.heater_generator.join(','))
  if (filters.heater_water_generator?.length)
    params.set(
      'heater_water_generator',
      filters.heater_water_generator.join(',')
    )
  if (filters.power_source_heater?.length)
    params.set('power_source_heater', filters.power_source_heater.join(','))
  if (filters.power_source_water_heater?.length)
    params.set(
      'power_source_water_heater',
      filters.power_source_water_heater.join(',')
    )
  if (filters.minergie?.length)
    params.set('minergie', filters.minergie.join(','))

  // Environmental filters - arrays
  if (filters.noise_exposure?.length)
    params.set('noise_exposure', filters.noise_exposure.join(','))
  if (filters.travel_time_to_centers?.length)
    params.set(
      'travel_time_to_centers',
      filters.travel_time_to_centers.join(',')
    )
  if (filters.slope?.length) params.set('slope', filters.slope.join(','))
  if (filters.tax_burden?.length)
    params.set('tax_burden', filters.tax_burden.join(','))
  if (filters.distance_to_highvoltage_powerlines?.length)
    params.set(
      'distance_to_highvoltage_powerlines',
      filters.distance_to_highvoltage_powerlines.join(',')
    )
  if (filters.mountain_view?.length)
    params.set('mountain_view', filters.mountain_view.join(','))
  if (filters.lake_view?.length)
    params.set('lake_view', filters.lake_view.join(','))
  if (filters.public_transport_quality?.length)
    params.set(
      'public_transport_quality',
      filters.public_transport_quality.join(',')
    )

  // Range fields
  if (filters.area?.min !== undefined)
    params.set('area_min', filters.area.min.toString())
  if (filters.area?.max !== undefined)
    params.set('area_max', filters.area.max.toString())
  if (filters.building_year?.min !== undefined)
    params.set('building_year_min', filters.building_year.min.toString())
  if (filters.building_year?.max !== undefined)
    params.set('building_year_max', filters.building_year.max.toString())
  if (filters.renovation_year?.min !== undefined)
    params.set('renovation_year_min', filters.renovation_year.min.toString())
  if (filters.renovation_year?.max !== undefined)
    params.set('renovation_year_max', filters.renovation_year.max.toString())
  if (filters.floor_nb?.min !== undefined)
    params.set('floor_nb_min', filters.floor_nb.min.toString())
  if (filters.floor_nb?.max !== undefined)
    params.set('floor_nb_max', filters.floor_nb.max.toString())
  if (filters.unit_nb?.min !== undefined)
    params.set('unit_nb_min', filters.unit_nb.min.toString())
  if (filters.unit_nb?.max !== undefined)
    params.set('unit_nb_max', filters.unit_nb.max.toString())
  if (filters.height?.min !== undefined)
    params.set('height_min', filters.height.min.toString())
  if (filters.height?.max !== undefined)
    params.set('height_max', filters.height.max.toString())
  if (filters.building_volume?.min !== undefined)
    params.set('building_volume_min', filters.building_volume.min.toString())
  if (filters.building_volume?.max !== undefined)
    params.set('building_volume_max', filters.building_volume.max.toString())
  if (filters.gross_floor_area?.min !== undefined)
    params.set('gross_floor_area_min', filters.gross_floor_area.min.toString())
  if (filters.gross_floor_area?.max !== undefined)
    params.set('gross_floor_area_max', filters.gross_floor_area.max.toString())
  if (filters.protected_rank?.min !== undefined)
    params.set('protected_rank_min', filters.protected_rank.min.toString())
  if (filters.protected_rank?.max !== undefined)
    params.set('protected_rank_max', filters.protected_rank.max.toString())
  if (filters.idc?.min !== undefined)
    params.set('idc_min', filters.idc.min.toString())
  if (filters.idc?.max !== undefined)
    params.set('idc_max', filters.idc.max.toString())

  // Solar range fields
  if (filters.solar_area?.min !== undefined)
    params.set('solar_area_min', filters.solar_area.min.toString())
  if (filters.solar_area?.max !== undefined)
    params.set('solar_area_max', filters.solar_area.max.toString())
  if (filters.solar_roof_pitch?.min !== undefined)
    params.set('solar_roof_pitch_min', filters.solar_roof_pitch.min.toString())
  if (filters.solar_roof_pitch?.max !== undefined)
    params.set('solar_roof_pitch_max', filters.solar_roof_pitch.max.toString())
  if (filters.solar_global_exposure?.min !== undefined)
    params.set(
      'solar_global_exposure_min',
      filters.solar_global_exposure.min.toString()
    )
  if (filters.solar_global_exposure?.max !== undefined)
    params.set(
      'solar_global_exposure_max',
      filters.solar_global_exposure.max.toString()
    )
  if (filters.solar_average_exposure?.min !== undefined)
    params.set(
      'solar_average_exposure_min',
      filters.solar_average_exposure.min.toString()
    )
  if (filters.solar_average_exposure?.max !== undefined)
    params.set(
      'solar_average_exposure_max',
      filters.solar_average_exposure.max.toString()
    )
  if (filters.solar_electrical_efficiency?.min !== undefined)
    params.set(
      'solar_electrical_efficiency_min',
      filters.solar_electrical_efficiency.min.toString()
    )
  if (filters.solar_electrical_efficiency?.max !== undefined)
    params.set(
      'solar_electrical_efficiency_max',
      filters.solar_electrical_efficiency.max.toString()
    )
  if (filters.solar_thermal_efficiency?.min !== undefined)
    params.set(
      'solar_thermal_efficiency_min',
      filters.solar_thermal_efficiency.min.toString()
    )
  if (filters.solar_thermal_efficiency?.max !== undefined)
    params.set(
      'solar_thermal_efficiency_max',
      filters.solar_thermal_efficiency.max.toString()
    )
  if (filters.solar_installed_power?.min !== undefined)
    params.set(
      'solar_installed_power_min',
      filters.solar_installed_power.min.toString()
    )
  if (filters.solar_installed_power?.max !== undefined)
    params.set(
      'solar_installed_power_max',
      filters.solar_installed_power.max.toString()
    )
  if (filters.solar_installed_first_date?.min)
    params.set(
      'solar_installed_first_date_min',
      filters.solar_installed_first_date.min
    )
  if (filters.solar_installed_first_date?.max)
    params.set(
      'solar_installed_first_date_max',
      filters.solar_installed_first_date.max
    )

  // Boolean filters
  if (filters.has_civil_protection_shelter !== undefined)
    params.set(
      'has_civil_protection_shelter',
      filters.has_civil_protection_shelter.toString()
    )
  if (filters.active !== undefined)
    params.set('active', filters.active.toString())
  if (filters.new_construction !== undefined)
    params.set('new_construction', filters.new_construction.toString())
  if (filters.is_furnished !== undefined)
    params.set('is_furnished', filters.is_furnished.toString())
  if (filters.pets_allowed !== undefined)
    params.set('pets_allowed', filters.pets_allowed.toString())
  if (filters.wheelchair_accessible !== undefined)
    params.set(
      'wheelchair_accessible',
      filters.wheelchair_accessible.toString()
    )
  if (filters.exact_location !== undefined)
    params.set('exact_location', filters.exact_location.toString())

  // User filters
  if (filters.building_ids?.length)
    params.set('building_ids', filters.building_ids.join(','))
  if (filters.tags?.length) params.set('tags', filters.tags.join(','))

  // Ownership filters
  if (filters.owners?.min !== undefined)
    params.set('owners_min', filters.owners.min.toString())
  if (filters.owners?.max !== undefined)
    params.set('owners_max', filters.owners.max.toString())
  if (filters.owners_type) params.set('owners_type', filters.owners_type)

  // Land filters
  if (filters.land_area?.min !== undefined)
    params.set('land_area_min', filters.land_area.min.toString())
  if (filters.land_area?.max !== undefined)
    params.set('land_area_max', filters.land_area.max.toString())

  // History filters
  if (filters.lands_transaction_number?.min !== undefined)
    params.set(
      'lands_transaction_number_min',
      filters.lands_transaction_number.min.toString()
    )
  if (filters.lands_transaction_number?.max !== undefined)
    params.set(
      'lands_transaction_number_max',
      filters.lands_transaction_number.max.toString()
    )
  if (filters.lands_transactions_date?.min)
    params.set(
      'lands_transactions_date_min',
      filters.lands_transactions_date.min
    )
  if (filters.lands_transactions_date?.max)
    params.set(
      'lands_transactions_date_max',
      filters.lands_transactions_date.max
    )
  if (filters.lands_transactions_price?.min !== undefined)
    params.set(
      'lands_transactions_price_min',
      filters.lands_transactions_price.min.toString()
    )
  if (filters.lands_transactions_price?.max !== undefined)
    params.set(
      'lands_transactions_price_max',
      filters.lands_transactions_price.max.toString()
    )
  if (filters.lands_building_permit_number?.min !== undefined)
    params.set(
      'lands_building_permit_number_min',
      filters.lands_building_permit_number.min.toString()
    )
  if (filters.lands_building_permit_number?.max !== undefined)
    params.set(
      'lands_building_permit_number_max',
      filters.lands_building_permit_number.max.toString()
    )
  if (filters.lands_building_permits_date?.min)
    params.set(
      'lands_building_permits_date_min',
      filters.lands_building_permits_date.min
    )
  if (filters.lands_building_permits_date?.max)
    params.set(
      'lands_building_permits_date_max',
      filters.lands_building_permits_date.max
    )
  if (filters.listing_rent_number?.min !== undefined)
    params.set(
      'listing_rent_number_min',
      filters.listing_rent_number.min.toString()
    )
  if (filters.listing_rent_number?.max !== undefined)
    params.set(
      'listing_rent_number_max',
      filters.listing_rent_number.max.toString()
    )
  if (filters.listing_sale_number?.min !== undefined)
    params.set(
      'listing_sale_number_min',
      filters.listing_sale_number.min.toString()
    )
  if (filters.listing_sale_number?.max !== undefined)
    params.set(
      'listing_sale_number_max',
      filters.listing_sale_number.max.toString()
    )
  if (filters.listings_listing_date?.min)
    params.set('listings_listing_date_min', filters.listings_listing_date.min)
  if (filters.listings_listing_date?.max)
    params.set('listings_listing_date_max', filters.listings_listing_date.max)
  if (filters.listings_rent_date?.min)
    params.set('listings_rent_date_min', filters.listings_rent_date.min)
  if (filters.listings_rent_date?.max)
    params.set('listings_rent_date_max', filters.listings_rent_date.max)
  if (filters.listings_sale_date?.min)
    params.set('listings_sale_date_min', filters.listings_sale_date.min)
  if (filters.listings_sale_date?.max)
    params.set('listings_sale_date_max', filters.listings_sale_date.max)
  if (filters.listings_price?.min !== undefined)
    params.set('listings_price_min', filters.listings_price.min.toString())
  if (filters.listings_price?.max !== undefined)
    params.set('listings_price_max', filters.listings_price.max.toString())
  if (filters.listings_price_per_square_meter?.min !== undefined)
    params.set(
      'listings_price_per_square_meter_min',
      filters.listings_price_per_square_meter.min.toString()
    )
  if (filters.listings_price_per_square_meter?.max !== undefined)
    params.set(
      'listings_price_per_square_meter_max',
      filters.listings_price_per_square_meter.max.toString()
    )

  return params
}

// Helper to parse URL params to filters
const urlParamsToFilters = (params: URLSearchParams): BuildingSearchFilters => {
  const filters: BuildingSearchFilters = {}

  // Location fields - consistent with Land filters
  if (params.has('location_search'))
    filters.location_search = params.get('location_search') || ''
  if (params.has('location_type'))
    filters.location_type = params.get('location_type') as
      | 'region'
      | 'district'
      | 'municipality'
      | 'npa'
  if (params.has('municipality'))
    filters.municipality = params.get('municipality') || ''
  if (params.has('district')) filters.district = params.get('district') || ''
  if (params.has('region')) filters.region = params.get('region') || ''
  if (params.has('npa')) filters.npa = params.get('npa') || ''

  // Legacy location fields for API compatibility
  if (params.has('city_name')) filters.city_name = params.get('city_name') || ''
  if (params.has('municipality_name'))
    filters.municipality_name = params.get('municipality_name') || ''
  if (params.has('district_name'))
    filters.district_name = params.get('district_name') || ''
  if (params.has('region_name'))
    filters.region_name = params.get('region_name') || ''
  if (params.has('address_full'))
    filters.address_full = params.get('address_full') || ''
  if (params.has('building_id'))
    filters.building_id = params.get('building_id') || ''
  if (params.has('egid')) filters.egid = Number(params.get('egid'))

  // Array fields (location IDs)
  const cityId = params.get('city_id')
  if (cityId) filters.city_id = cityId.split(',')
  const municipalityId = params.get('municipality_id')
  if (municipalityId) filters.municipality_id = municipalityId.split(',')
  const districtId = params.get('district_id')
  if (districtId) filters.district_id = districtId.split(',')
  const regionId = params.get('region_id')
  if (regionId) filters.region_id = regionId.split(',')

  // Array fields for multi-select dropdowns
  const categoryParam = params.get('category')
  if (categoryParam) filters.category = categoryParam.split(',')
  const typeParam = params.get('type')
  if (typeParam) filters.type = typeParam.split(',')
  if (params.has('status')) filters.status = params.get('status') || ''
  const classificationParam = params.get('classification')
  if (classificationParam)
    filters.classification = classificationParam.split(',')
  const popetyClassificationParam = params.get('popety_classification')
  if (popetyClassificationParam)
    filters.popety_classification = popetyClassificationParam.split(',')
  const buildingPeriodParam = params.get('building_period')
  if (buildingPeriodParam)
    filters.building_period = buildingPeriodParam.split(',')
  const renovationPeriodParam = params.get('renovation_period')
  if (renovationPeriodParam)
    filters.renovation_period = renovationPeriodParam.split(',')
  const buildingPermitsClassificationParam = params.get(
    'building_permits_classification'
  )
  if (buildingPermitsClassificationParam)
    filters.building_permits_classification =
      buildingPermitsClassificationParam.split(',')

  // Energy filters - arrays
  const heaterGeneratorParam = params.get('heater_generator')
  if (heaterGeneratorParam)
    filters.heater_generator = heaterGeneratorParam.split(',')
  const heaterWaterGeneratorParam = params.get('heater_water_generator')
  if (heaterWaterGeneratorParam)
    filters.heater_water_generator = heaterWaterGeneratorParam.split(',')
  const powerSourceHeaterParam = params.get('power_source_heater')
  if (powerSourceHeaterParam)
    filters.power_source_heater = powerSourceHeaterParam.split(',')
  const powerSourceWaterHeaterParam = params.get('power_source_water_heater')
  if (powerSourceWaterHeaterParam)
    filters.power_source_water_heater = powerSourceWaterHeaterParam.split(',')
  const minergieParam = params.get('minergie')
  if (minergieParam) filters.minergie = minergieParam.split(',')

  // Environmental filters - arrays
  const noiseExposureParam = params.get('noise_exposure')
  if (noiseExposureParam) filters.noise_exposure = noiseExposureParam.split(',')
  const travelTimeToCentersParam = params.get('travel_time_to_centers')
  if (travelTimeToCentersParam)
    filters.travel_time_to_centers = travelTimeToCentersParam.split(',')
  const slopeParam = params.get('slope')
  if (slopeParam) filters.slope = slopeParam.split(',')
  const taxBurdenParam = params.get('tax_burden')
  if (taxBurdenParam) filters.tax_burden = taxBurdenParam.split(',')
  const distanceToHighvoltagePowerlinesParam = params.get(
    'distance_to_highvoltage_powerlines'
  )
  if (distanceToHighvoltagePowerlinesParam)
    filters.distance_to_highvoltage_powerlines =
      distanceToHighvoltagePowerlinesParam.split(',')
  const mountainViewParam = params.get('mountain_view')
  if (mountainViewParam) filters.mountain_view = mountainViewParam.split(',')
  const lakeViewParam = params.get('lake_view')
  if (lakeViewParam) filters.lake_view = lakeViewParam.split(',')
  const publicTransportQualityParam = params.get('public_transport_quality')
  if (publicTransportQualityParam)
    filters.public_transport_quality = publicTransportQualityParam.split(',')

  // User filters
  const buildingIdsParam = params.get('building_ids')
  if (buildingIdsParam) filters.building_ids = buildingIdsParam.split(',')
  const tagsParam = params.get('tags')
  if (tagsParam) filters.tags = tagsParam.split(',')

  // Boolean filters
  if (params.has('has_civil_protection_shelter'))
    filters.has_civil_protection_shelter =
      params.get('has_civil_protection_shelter') === 'true'
  if (params.has('active')) filters.active = params.get('active') === 'true'
  if (params.has('new_construction'))
    filters.new_construction = params.get('new_construction') === 'true'
  if (params.has('is_furnished'))
    filters.is_furnished = params.get('is_furnished') === 'true'
  if (params.has('pets_allowed'))
    filters.pets_allowed = params.get('pets_allowed') === 'true'
  if (params.has('wheelchair_accessible'))
    filters.wheelchair_accessible =
      params.get('wheelchair_accessible') === 'true'
  if (params.has('exact_location'))
    filters.exact_location = params.get('exact_location') === 'true'

  // Range fields
  if (params.has('area_min') || params.has('area_max')) {
    filters.area = {}
    if (params.has('area_min'))
      filters.area.min = Number(params.get('area_min'))
    if (params.has('area_max'))
      filters.area.max = Number(params.get('area_max'))
  }
  if (params.has('building_year_min') || params.has('building_year_max')) {
    filters.building_year = {}
    if (params.has('building_year_min'))
      filters.building_year.min = Number(params.get('building_year_min'))
    if (params.has('building_year_max'))
      filters.building_year.max = Number(params.get('building_year_max'))
  }
  if (params.has('floor_nb_min') || params.has('floor_nb_max')) {
    filters.floor_nb = {}
    if (params.has('floor_nb_min'))
      filters.floor_nb.min = Number(params.get('floor_nb_min'))
    if (params.has('floor_nb_max'))
      filters.floor_nb.max = Number(params.get('floor_nb_max'))
  }
  if (params.has('unit_nb_min') || params.has('unit_nb_max')) {
    filters.unit_nb = {}
    if (params.has('unit_nb_min'))
      filters.unit_nb.min = Number(params.get('unit_nb_min'))
    if (params.has('unit_nb_max'))
      filters.unit_nb.max = Number(params.get('unit_nb_max'))
  }
  if (params.has('height_min') || params.has('height_max')) {
    filters.height = {}
    if (params.has('height_min'))
      filters.height.min = Number(params.get('height_min'))
    if (params.has('height_max'))
      filters.height.max = Number(params.get('height_max'))
  }
  if (params.has('building_volume_min') || params.has('building_volume_max')) {
    filters.building_volume = {}
    if (params.has('building_volume_min'))
      filters.building_volume.min = Number(params.get('building_volume_min'))
    if (params.has('building_volume_max'))
      filters.building_volume.max = Number(params.get('building_volume_max'))
  }
  if (params.has('renovation_year_min') || params.has('renovation_year_max')) {
    filters.renovation_year = {}
    if (params.has('renovation_year_min'))
      filters.renovation_year.min = Number(params.get('renovation_year_min'))
    if (params.has('renovation_year_max'))
      filters.renovation_year.max = Number(params.get('renovation_year_max'))
  }
  if (
    params.has('gross_floor_area_min') ||
    params.has('gross_floor_area_max')
  ) {
    filters.gross_floor_area = {}
    if (params.has('gross_floor_area_min'))
      filters.gross_floor_area.min = Number(params.get('gross_floor_area_min'))
    if (params.has('gross_floor_area_max'))
      filters.gross_floor_area.max = Number(params.get('gross_floor_area_max'))
  }
  if (params.has('protected_rank_min') || params.has('protected_rank_max')) {
    filters.protected_rank = {}
    if (params.has('protected_rank_min'))
      filters.protected_rank.min = Number(params.get('protected_rank_min'))
    if (params.has('protected_rank_max'))
      filters.protected_rank.max = Number(params.get('protected_rank_max'))
  }
  if (params.has('idc_min') || params.has('idc_max')) {
    filters.idc = {}
    if (params.has('idc_min')) filters.idc.min = Number(params.get('idc_min'))
    if (params.has('idc_max')) filters.idc.max = Number(params.get('idc_max'))
  }

  // Solar range fields
  if (params.has('solar_area_min') || params.has('solar_area_max')) {
    filters.solar_area = {}
    if (params.has('solar_area_min'))
      filters.solar_area.min = Number(params.get('solar_area_min'))
    if (params.has('solar_area_max'))
      filters.solar_area.max = Number(params.get('solar_area_max'))
  }
  if (
    params.has('solar_roof_pitch_min') ||
    params.has('solar_roof_pitch_max')
  ) {
    filters.solar_roof_pitch = {}
    if (params.has('solar_roof_pitch_min'))
      filters.solar_roof_pitch.min = Number(params.get('solar_roof_pitch_min'))
    if (params.has('solar_roof_pitch_max'))
      filters.solar_roof_pitch.max = Number(params.get('solar_roof_pitch_max'))
  }
  if (
    params.has('solar_global_exposure_min') ||
    params.has('solar_global_exposure_max')
  ) {
    filters.solar_global_exposure = {}
    if (params.has('solar_global_exposure_min'))
      filters.solar_global_exposure.min = Number(
        params.get('solar_global_exposure_min')
      )
    if (params.has('solar_global_exposure_max'))
      filters.solar_global_exposure.max = Number(
        params.get('solar_global_exposure_max')
      )
  }
  if (
    params.has('solar_average_exposure_min') ||
    params.has('solar_average_exposure_max')
  ) {
    filters.solar_average_exposure = {}
    if (params.has('solar_average_exposure_min'))
      filters.solar_average_exposure.min = Number(
        params.get('solar_average_exposure_min')
      )
    if (params.has('solar_average_exposure_max'))
      filters.solar_average_exposure.max = Number(
        params.get('solar_average_exposure_max')
      )
  }
  if (
    params.has('solar_electrical_efficiency_min') ||
    params.has('solar_electrical_efficiency_max')
  ) {
    filters.solar_electrical_efficiency = {}
    if (params.has('solar_electrical_efficiency_min'))
      filters.solar_electrical_efficiency.min = Number(
        params.get('solar_electrical_efficiency_min')
      )
    if (params.has('solar_electrical_efficiency_max'))
      filters.solar_electrical_efficiency.max = Number(
        params.get('solar_electrical_efficiency_max')
      )
  }
  if (
    params.has('solar_thermal_efficiency_min') ||
    params.has('solar_thermal_efficiency_max')
  ) {
    filters.solar_thermal_efficiency = {}
    if (params.has('solar_thermal_efficiency_min'))
      filters.solar_thermal_efficiency.min = Number(
        params.get('solar_thermal_efficiency_min')
      )
    if (params.has('solar_thermal_efficiency_max'))
      filters.solar_thermal_efficiency.max = Number(
        params.get('solar_thermal_efficiency_max')
      )
  }
  if (
    params.has('solar_installed_power_min') ||
    params.has('solar_installed_power_max')
  ) {
    filters.solar_installed_power = {}
    if (params.has('solar_installed_power_min'))
      filters.solar_installed_power.min = Number(
        params.get('solar_installed_power_min')
      )
    if (params.has('solar_installed_power_max'))
      filters.solar_installed_power.max = Number(
        params.get('solar_installed_power_max')
      )
  }
  if (
    params.has('solar_installed_first_date_min') ||
    params.has('solar_installed_first_date_max')
  ) {
    filters.solar_installed_first_date = {}
    if (params.has('solar_installed_first_date_min'))
      filters.solar_installed_first_date.min =
        params.get('solar_installed_first_date_min') || ''
    if (params.has('solar_installed_first_date_max'))
      filters.solar_installed_first_date.max =
        params.get('solar_installed_first_date_max') || ''
  }

  // Ownership filters
  if (params.has('owners_min') || params.has('owners_max')) {
    filters.owners = {}
    if (params.has('owners_min'))
      filters.owners.min = Number(params.get('owners_min'))
    if (params.has('owners_max'))
      filters.owners.max = Number(params.get('owners_max'))
  }
  if (params.has('owners_type'))
    filters.owners_type = params.get('owners_type') || ''

  // Land filters
  if (params.has('land_area_min') || params.has('land_area_max')) {
    filters.land_area = {}
    if (params.has('land_area_min'))
      filters.land_area.min = Number(params.get('land_area_min'))
    if (params.has('land_area_max'))
      filters.land_area.max = Number(params.get('land_area_max'))
  }

  // History filters
  if (
    params.has('lands_transaction_number_min') ||
    params.has('lands_transaction_number_max')
  ) {
    filters.lands_transaction_number = {}
    if (params.has('lands_transaction_number_min'))
      filters.lands_transaction_number.min = Number(
        params.get('lands_transaction_number_min')
      )
    if (params.has('lands_transaction_number_max'))
      filters.lands_transaction_number.max = Number(
        params.get('lands_transaction_number_max')
      )
  }
  if (
    params.has('lands_transactions_date_min') ||
    params.has('lands_transactions_date_max')
  ) {
    filters.lands_transactions_date = {}
    if (params.has('lands_transactions_date_min'))
      filters.lands_transactions_date.min =
        params.get('lands_transactions_date_min') || ''
    if (params.has('lands_transactions_date_max'))
      filters.lands_transactions_date.max =
        params.get('lands_transactions_date_max') || ''
  }
  if (
    params.has('lands_transactions_price_min') ||
    params.has('lands_transactions_price_max')
  ) {
    filters.lands_transactions_price = {}
    if (params.has('lands_transactions_price_min'))
      filters.lands_transactions_price.min = Number(
        params.get('lands_transactions_price_min')
      )
    if (params.has('lands_transactions_price_max'))
      filters.lands_transactions_price.max = Number(
        params.get('lands_transactions_price_max')
      )
  }
  if (
    params.has('lands_building_permit_number_min') ||
    params.has('lands_building_permit_number_max')
  ) {
    filters.lands_building_permit_number = {}
    if (params.has('lands_building_permit_number_min'))
      filters.lands_building_permit_number.min = Number(
        params.get('lands_building_permit_number_min')
      )
    if (params.has('lands_building_permit_number_max'))
      filters.lands_building_permit_number.max = Number(
        params.get('lands_building_permit_number_max')
      )
  }
  if (
    params.has('lands_building_permits_date_min') ||
    params.has('lands_building_permits_date_max')
  ) {
    filters.lands_building_permits_date = {}
    if (params.has('lands_building_permits_date_min'))
      filters.lands_building_permits_date.min =
        params.get('lands_building_permits_date_min') || ''
    if (params.has('lands_building_permits_date_max'))
      filters.lands_building_permits_date.max =
        params.get('lands_building_permits_date_max') || ''
  }
  if (
    params.has('listing_rent_number_min') ||
    params.has('listing_rent_number_max')
  ) {
    filters.listing_rent_number = {}
    if (params.has('listing_rent_number_min'))
      filters.listing_rent_number.min = Number(
        params.get('listing_rent_number_min')
      )
    if (params.has('listing_rent_number_max'))
      filters.listing_rent_number.max = Number(
        params.get('listing_rent_number_max')
      )
  }
  if (
    params.has('listing_sale_number_min') ||
    params.has('listing_sale_number_max')
  ) {
    filters.listing_sale_number = {}
    if (params.has('listing_sale_number_min'))
      filters.listing_sale_number.min = Number(
        params.get('listing_sale_number_min')
      )
    if (params.has('listing_sale_number_max'))
      filters.listing_sale_number.max = Number(
        params.get('listing_sale_number_max')
      )
  }
  if (
    params.has('listings_listing_date_min') ||
    params.has('listings_listing_date_max')
  ) {
    filters.listings_listing_date = {}
    if (params.has('listings_listing_date_min'))
      filters.listings_listing_date.min =
        params.get('listings_listing_date_min') || ''
    if (params.has('listings_listing_date_max'))
      filters.listings_listing_date.max =
        params.get('listings_listing_date_max') || ''
  }
  if (
    params.has('listings_rent_date_min') ||
    params.has('listings_rent_date_max')
  ) {
    filters.listings_rent_date = {}
    if (params.has('listings_rent_date_min'))
      filters.listings_rent_date.min =
        params.get('listings_rent_date_min') || ''
    if (params.has('listings_rent_date_max'))
      filters.listings_rent_date.max =
        params.get('listings_rent_date_max') || ''
  }
  if (
    params.has('listings_sale_date_min') ||
    params.has('listings_sale_date_max')
  ) {
    filters.listings_sale_date = {}
    if (params.has('listings_sale_date_min'))
      filters.listings_sale_date.min =
        params.get('listings_sale_date_min') || ''
    if (params.has('listings_sale_date_max'))
      filters.listings_sale_date.max =
        params.get('listings_sale_date_max') || ''
  }
  if (params.has('listings_price_min') || params.has('listings_price_max')) {
    filters.listings_price = {}
    if (params.has('listings_price_min'))
      filters.listings_price.min = Number(params.get('listings_price_min'))
    if (params.has('listings_price_max'))
      filters.listings_price.max = Number(params.get('listings_price_max'))
  }
  if (
    params.has('listings_price_per_square_meter_min') ||
    params.has('listings_price_per_square_meter_max')
  ) {
    filters.listings_price_per_square_meter = {}
    if (params.has('listings_price_per_square_meter_min'))
      filters.listings_price_per_square_meter.min = Number(
        params.get('listings_price_per_square_meter_min')
      )
    if (params.has('listings_price_per_square_meter_max'))
      filters.listings_price_per_square_meter.max = Number(
        params.get('listings_price_per_square_meter_max')
      )
  }

  return filters
}

export const useBuildingsUrl = (propertyPanelOpen: boolean = false) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Parse current URL state
  const getUrlState = useCallback((): UrlState => {
    const params = new URLSearchParams(location.search)

    // Parse filters
    const filters = urlParamsToFilters(params)

    // Parse building-specific view (separate from global view parameter)
    const view = (params.get('buildingView') as ViewType) || 'list'

    // Parse sort - use building-specific params to avoid conflict with lands sort
    const sort: BuildingSearchSort[] = []
    const sortField = params.get('building_sort_field')
    const sortOrder = params.get('building_sort_order') as 'asc' | 'desc' | null
    if (sortField && sortOrder) {
      sort.push({ field: sortField, order: sortOrder })
    }

    // Parse pagination
    const page = Number(params.get('page')) || 1
    const pageSize = Number(params.get('page_size')) || 20

    return { filters, view, sort, page, pageSize }
  }, [location.search])

  // Update URL with new state
  const updateUrl = useCallback(
    (updates: Partial<UrlState>) => {
      const currentState = getUrlState()
      const newState = { ...currentState, ...updates }
      console.log('Updating URL state:', newState)

      // Build new URL params, preserving existing non-building params
      const currentParams = new URLSearchParams(location.search)
      const params = new URLSearchParams()

      // Preserve important global parameters (like mode, map view mode, etc.)
      // Also preserve lands sort params so they aren't lost when updating building URL
      const globalParamsToPreserve = [
        'mode',
        'dimension',
        'basemap',
        'activeLayer',
        'more',
        'transport',
        'duration',
        'lat',
        'lon',
        'zoom',
        'sort_field', // Lands sort field
        'sort_order', // Lands sort order
        // Only preserve buildingId if panel is open (to allow closePropertyPanel to remove it)
        ...(propertyPanelOpen ? ['buildingId'] : []),
      ]

      globalParamsToPreserve.forEach((param) => {
        if (currentParams.has(param)) {
          params.set(param, currentParams.get(param)!)
        }
      })

      // Preserve global view mode (map vs list) separately from building view mode
      if (currentParams.has('view')) {
        params.set('view', currentParams.get('view')!)
      }

      // Add filters
      const filterParams = filtersToUrlParams(newState.filters)
      filterParams.forEach((value, key) => params.set(key, value))

      // Add building-specific view mode (use different parameter name to avoid conflict)
      if (newState.view !== 'list') params.set('buildingView', newState.view)

      // Add sort - use building-specific params to avoid conflict with lands sort
      if (newState.sort.length > 0) {
        params.set('building_sort_field', newState.sort[0].field)
        params.set('building_sort_order', newState.sort[0].order)
      }

      // Add pagination
      if (newState.page > 1) params.set('page', newState.page.toString())
      if (newState.pageSize !== 20)
        params.set('page_size', newState.pageSize.toString())

      // Update URL without triggering navigation
      const newSearch = params.toString()
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname

      navigate(newUrl, { replace: true })
    },
    [navigate, location.pathname, location.search, getUrlState, propertyPanelOpen]
  )

  // Individual update methods
  const updateFilters = useCallback(
    (filters: BuildingSearchFilters) => {
      console.log('Updating filters in URL:', filters)
      updateUrl({ filters })
    },
    [updateUrl]
  )

  const updateView = useCallback(
    (view: ViewType) => {
      updateUrl({ view })
    },
    [updateUrl]
  )

  const updateSort = useCallback(
    (sort: BuildingSearchSort[]) => {
      updateUrl({ sort })
    },
    [updateUrl]
  )

  const updatePagination = useCallback(
    (page: number, pageSize: number) => {
      updateUrl({ page, pageSize })
    },
    [updateUrl]
  )

  return {
    urlState: getUrlState(),
    updateFilters,
    updateView,
    updateSort,
    updatePagination,
    updateUrl,
  }
}
